<template>
  <div>
    <b-nav-item-dropdown
      id="helpdesk-dropdown"
      ref="helpdeskDropdown"
      class="dropdown-notification mr-75"
      menu-class="dropdown-menu-media dropdown-menu-right"
      left
    >
      <template #button-content>
        <feather-icon
          :badge="notifications.length"
          badge-classes="bg-danger"
          class="text-body"
          icon="HelpCircleIcon"
          size="21"
          v-b-tooltip.hover.button.viewport="`${$t('helpdesk.title')}`"
        />
      </template>

      <!-- Header -->
      <li class="dropdown-menu-header">
        <div class="dropdown-header d-flex">
          <h4 class="notification-title mb-0 mr-auto">
            <router-link :to="{ name: 'helpdesk_tickets' }">
              <feather-icon icon="LinkIcon" />
              <span class="align-middle ml-50">
                {{ $tc("helpdesk.ticket.title", 2) }}
              </span>
            </router-link>
          </h4>

          <div>
            <b-button
              v-if="$can('create', 'HelpdeskTicket')"
              v-b-modal.create_ticket_modal_notif
              size="sm"
              variant="info"
              style="margin-right: 5px"
            >
              {{ $t("helpdesk.ticket.create_ticket") }}
            </b-button>

            <b-badge pill variant="light-primary">
              {{ `${notifications.length} ${$tc("helpdesk.ticket.title", 2)}` }}
            </b-badge>
          </div>
        </div>
      </li>

      <!-- Notifications -->
      <vue-perfect-scrollbar
        :settings="perfectScrollbarSettings"
        class="scrollable-container media-list scroll-area"
        tagname="li"
      >
        <b-link
          v-for="notification in notifications"
          :key="notification.id"
          class="cursor-pointer"
          draggable
          @click="openTicket(notification)"
          @dragstart="onDragStart(notification)"
          @dragend="onDragEnd"
        >
          <b-media>
            <template #aside>
              <b-avatar size="32">
                <span class="d-flex align-items-center">
                  <feather-icon
                    :icon="notification.error ? 'CollectionIcon' : 'FileIcon'"
                    size="16"
                  />
                </span>
              </b-avatar>
            </template>

            <p class="media-heading">
              <span class="font-weight-bolder">
                {{ notification.title }}

                <small
                  class="notification-text float-right"
                  :class="{
                    'text-danger font-weight-bold': slaTimeExpired(notification.id),
                  }"
                >
                  {{ slaTimeLabel(notification.id) }}
                </small>
              </span>
            </p>

            <small class="notification-text">
              {{ notification.body }}
            </small>

            <p class="media-heading">
              <small class="notification-text">
                <b-badge pill variant="light-primary">
                  {{ notification.tag }}
                </b-badge>
              </small>
            </p>

            <!-- Barra SLA por ticket -->
            <sla-bar-card
              compact
              :priority="notification.sla.priority"
              :priority-label="notification.sla.priorityName"
              :total-seconds="notification.sla.totalSeconds"
              :remaining-seconds="notification.sla.remainingSeconds"
              :running="notification.sla.running"
              @tick="onSlaTick(notification.id, $event)"
            />
          </b-media>
        </b-link>
      </vue-perfect-scrollbar>
    </b-nav-item-dropdown>

    <!-- El modal vive fuera del dropdown -->
    <create-ticket-modal modal-id="create_ticket_modal_notif" />
  </div>
</template>

<script>
import {
  BNavItemDropdown,
  BButton,
  BBadge,
  BLink,
  BMedia,
  BAvatar,
} from "bootstrap-vue";

import VuePerfectScrollbar from "vue-perfect-scrollbar";
import store from "@/store";
import { computed, ref } from "@vue/composition-api";
import LayoutApi from "@/libs/LayoutApi.js";
import { SLOT_TYPES } from "@/config/layoutConfig";
import CreateTicketModal from "@/views/components/GridView/layouts/slots/HelpdeskSlot/CreateTicketModal.vue";
import SlaBarCard from "./SlaBardCard.vue";

const layoutApi = new LayoutApi(store);

const slaPriority = level => {
  if (level >= 6) return "critical";
  if (level >= 4) return "high";
  if (level >= 3) return "medium";
  return "low";
};

const toNotification = ticket => {
  const limit =
    ((ticket.priority && ticket.priority.days_solution) || 0) * 24 * 60 * 60;

  let accumulated = ticket.sla_accumulated_seconds || 0;

  if (ticket.sla_clock_running && ticket.sla_started_at) {
    accumulated += Math.max(
      0,
      Math.floor((Date.now() - new Date(ticket.sla_started_at).getTime()) / 1000)
    );
  }

  return {
    id: ticket.id,
    title: ticket.subject || `Ticket #${ticket.id}`,
    body: ticket.message || "",
    tag: (ticket.status && ticket.status.name) || "",
    error: !!ticket.sla_expired,
    sla: {
      priority: slaPriority(ticket.priority && ticket.priority.level),
      priorityName: (ticket.priority && ticket.priority.name) || null,
      totalSeconds: limit,
      remainingSeconds: Math.max(0, limit - accumulated),
      running: !!ticket.sla_clock_running,
    },
  };
};

export default {
  components: {
    BNavItemDropdown,
    BButton,
    BBadge,
    BLink,
    BMedia,
    BAvatar,
    VuePerfectScrollbar,
    CreateTicketModal,
    SlaBarCard,
  },

  setup(_, context) {
    const helpdeskDropdown = ref(null);
    const isDragging = ref(false);

    store.dispatch("helpdesk/getMyOpenTickets").catch(() => {});

    const HIDDEN_PRIORITIES = ["VERY_LOW", "VERY_HIGH"];

    const notifications = computed(() =>
      (store.state.helpdesk.helpdeskNotifications || [])
        .filter(
          ticket =>
            !HIDDEN_PRIORITIES.includes(
              ticket.priority && ticket.priority.code
            )
        )
        .map(toNotification)
    );

    const slaTimes = ref({});

    const onSlaTick = (id, payload) => {
      slaTimes.value = {
        ...slaTimes.value,
        [id]: payload,
      };
    };

    const slaTimeLabel = id =>
      slaTimes.value[id] ? slaTimes.value[id].label : "";

    const slaTimeExpired = id =>
      slaTimes.value[id] ? slaTimes.value[id].expired : false;

    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    const getDropdownRef = () => {
      if (context.refs && context.refs.helpdeskDropdown) {
        return context.refs.helpdeskDropdown;
      }

      return helpdeskDropdown.value;
    };

    const closeDropdown = () => {
      context.root.$nextTick(() => {
        const dropdown = getDropdownRef();

        if (dropdown && typeof dropdown.hide === "function") {
          dropdown.hide(true);
          return;
        }

        context.root.$root.$emit("bv::hide::dropdown", "helpdesk-dropdown");
      });
    };

    const openTicket = ticket => {
      if (isDragging.value) return;

      closeDropdown();

      context.root.$router
        .push({
          name: "helpdesk_ticket_details",
          params: { id: ticket.id },
        })
        .catch(() => {});
    };

    const onDragStart = ticket => {
      isDragging.value = true;

      layoutApi.setSlotData(SLOT_TYPES.HELPDESK, "HELPDESK", {
        data: ticket,
      });

      store.dispatch("grid/updateUserConfig");
    };

    const onDragEnd = () => {
      closeDropdown();

      setTimeout(() => {
        isDragging.value = false;
      }, 150);
    };

    return {
      helpdeskDropdown,
      isDragging,
      notifications,
      slaTimes,
      onSlaTick,
      slaTimeLabel,
      slaTimeExpired,
      perfectScrollbarSettings,
      closeDropdown,
      openTicket,
      onDragStart,
      onDragEnd,
    };
  },
};
</script>

<style scoped>
</style>